import { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { BsDownload } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import { downloadBlob } from '#lib/downloadBlob';

import { useExportContactListCsv } from '../../models/mutations';
import { useCustomColumns } from '../../models/queries';

// Keys mirror the backend's LIST_EXPORT_COLUMNS in contactLists/csvExport.js.
const STANDARD_COLUMNS = {
	contacts: [
		['salutation', 'Anrede'],
		['firstName', 'Vorname'],
		['lastName', 'Nachname'],
		['fullName', 'Name'],
		['companyRole', 'Position'],
		['email', 'E-Mail'],
		['phone', 'Telefon'],
		['companyName', 'Firmenname'],
		['street', 'Straße'],
		['postalCode', 'PLZ'],
		['city', 'Stadt'],
		['website', 'Website'],
	],
	companies: [
		['companyName', 'Firmenname'],
		['email', 'E-Mail'],
		['phone', 'Telefon'],
		['street', 'Straße'],
		['postalCode', 'PLZ'],
		['city', 'Stadt'],
		['website', 'Website'],
	],
};

const toggle = (list, key, checked) => (checked ? [...list, key] : list.filter((k) => k !== key));

/**
 * CSV export of one contact-list tab: standard fields plus the org's custom columns.
 */
const ContactListExportModal = ({ isOpen, onClose, listId, tab, scope, count, scopeLabel }) => {
	const { data: customColumns = [] } = useCustomColumns();
	const exportMutation = useExportContactListCsv();
	const [columns, setColumns] = useState([]);
	const [customColumnIds, setCustomColumnIds] = useState([]);

	useEffect(() => {
		if (!isOpen) return;
		setColumns(STANDARD_COLUMNS[tab].map(([key]) => key));
		setCustomColumnIds(customColumns.map((column) => column.id));
	}, [isOpen, tab, customColumns]);

	const exportCsv = async () => {
		const { blob, filename } = await exportMutation.mutateAsync({ listId, tab, columns, customColumnIds, scope });
		downloadBlob(blob, filename);
		onClose();
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="m">
			<Panel.Header icon={BsDownload} onClose={onClose} subtitle={scopeLabel}>
				Als CSV exportieren
			</Panel.Header>
			<Panel.Body className="space-y-4">
				<div>
					<p className="text-xs-head text-bgBase-800 mb-2">Stammdaten</p>
					<div className="grid grid-cols-2 gap-2">
						{STANDARD_COLUMNS[tab].map(([key, label]) => (
							<Checkbox
								key={key}
								id={`export-${key}`}
								label={label}
								checked={columns.includes(key)}
								onChange={(checked) => setColumns((prev) => toggle(prev, key, checked))}
							/>
						))}
					</div>
				</div>
				{customColumns.length > 0 && (
					<div>
						<p className="text-xs-head text-bgBase-800 mb-2">Eigene Spalten</p>
						<div className="grid grid-cols-2 gap-2">
							{customColumns.map((column) => (
								<Checkbox
									key={column.id}
									id={`export-custom-${column.id}`}
									label={column.name}
									checked={customColumnIds.includes(column.id)}
									onChange={(checked) =>
										setCustomColumnIds((prev) => toggle(prev, column.id, checked))
									}
								/>
							))}
						</div>
					</div>
				)}
			</Panel.Body>
			<Panel.Footer className="justify-end">
				<Button variant="outline" onClick={onClose}>
					Abbrechen
				</Button>
				<Button
					onClick={exportCsv}
					loading={exportMutation.isPending}
					disabled={count === 0 || columns.length + customColumnIds.length === 0}
				>
					Exportieren
				</Button>
			</Panel.Footer>
		</Modal>
	);
};

ContactListExportModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	listId: PropTypes.string.isRequired,
	tab: PropTypes.oneOf(['contacts', 'companies']).isRequired,
	// { ids } | { selectAll, filters } of the tab selection.
	scope: PropTypes.object.isRequired,
	count: PropTypes.number.isRequired,
	scopeLabel: PropTypes.string,
};

export default ContactListExportModal;
